/**
 * 後台 — 商品分類管理(#5 自訂分類)
 *
 * 列表所有分類(含停用)+ 新增 / 編輯(名稱 / 副標 / 圖 / 排序 / 上下架)+ 刪除。
 * 刪除:底下有商品時後端回 409,前端提示先清空。
 * 空集合:引導 seed 既有分類(即飲 / 鮮燉 / 自燉)。
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminListAllCategories,
  adminUpsertCategory,
  adminDeleteCategory,
  adminToggleCategoryActive,
  adminSeedCategories,
  type ProductCategoryMeta,
} from "../../api/admin";
import AdminHeader from "../../components/AdminHeader";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";

const PRODUCT_IMAGE_BASE = "https://storage.googleapis.com/yannwo-product-images";

type GuideSection = { title: string; body: string };

type FormState = {
  key: string; // 空 = 新增模式
  title: string;
  subtitle: string;
  image_filename: string;
  sort_order: string;
  active: boolean;
  usageIntro: string;
  usageSections: GuideSection[];
};

const EMPTY_FORM: FormState = {
  key: "",
  title: "",
  subtitle: "",
  image_filename: "",
  sort_order: "",
  active: true,
  usageIntro: "",
  usageSections: [],
};

export default function CategoryManage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const showToast = useUIStore((s) => s.showToast);

  const [cats, setCats] = useState<ProductCategoryMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  async function refresh() {
    setLoading(true);
    try {
      setCats(await adminListAllCategories());
    } catch {
      showToast("載入失敗", "error");
    } finally {
      setLoading(false);
    }
  }

  function startEdit(c: ProductCategoryMeta) {
    setForm({
      key: c.key,
      title: c.title,
      subtitle: c.subtitle || "",
      image_filename: c.image_filename || "",
      sort_order: String(c.sort_order ?? ""),
      active: c.active ?? true,
      usageIntro: c.usage_guide?.intro || "",
      usageSections: (c.usage_guide?.sections || []).map((s) => ({ title: s.title || "", body: s.body || "" })),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function addSection() {
    setForm((f) => ({ ...f, usageSections: [...f.usageSections, { title: "", body: "" }] }));
  }

  function updateSection(i: number, field: "title" | "body", value: string) {
    setForm((f) => ({
      ...f,
      usageSections: f.usageSections.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)),
    }));
  }

  function removeSection(i: number) {
    setForm((f) => ({ ...f, usageSections: f.usageSections.filter((_, idx) => idx !== i) }));
  }

  function resetForm() {
    setForm(EMPTY_FORM);
  }

  async function save() {
    if (!form.title.trim()) {
      showToast("請填寫分類名稱", "error");
      return;
    }
    setSubmitting(true);
    try {
      const r = await adminUpsertCategory({
        key: form.key || undefined,
        title: form.title.trim(),
        subtitle: form.subtitle.trim(),
        image_filename: form.image_filename.trim(),
        sort_order: parseInt(form.sort_order, 10) || 999,
        active: form.active,
        usage_guide: {
          intro: form.usageIntro.trim(),
          sections: form.usageSections
            .map((s) => ({ title: s.title.trim(), body: s.body.trim() }))
            .filter((s) => s.title || s.body),
        },
      });
      if (r.status === "ok") {
        showToast(r.created ? "已新增分類" : "已更新分類", "success");
        resetForm();
        await refresh();
      } else {
        showToast(`儲存未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleActive(c: ProductCategoryMeta) {
    try {
      const r = await adminToggleCategoryActive(c.key, !(c.active ?? true));
      if (r.status === "ok") {
        await refresh();
      } else {
        showToast(`操作未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    }
  }

  async function remove(c: ProductCategoryMeta) {
    if (!confirm(`確定刪除分類「${c.title}」?`)) return;
    try {
      const r = await adminDeleteCategory(c.key);
      if (r.status === "ok") {
        showToast("已刪除", "success");
        if (form.key === c.key) resetForm();
        await refresh();
      } else if (r.status === "has_products") {
        showToast(r.hint || "此分類底下還有商品,請先把商品移到其他分類或刪除", "error");
      } else {
        showToast(`刪除未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    }
  }

  async function runSeed() {
    setSeeding(true);
    try {
      const r = await adminSeedCategories(false);
      showToast(`匯入完成:新增 ${r.created} / 略過 ${r.skipped}`, "success");
      await refresh();
    } catch {
      showToast("匯入失敗", "error");
    } finally {
      setSeeding(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream">
      <AdminHeader active="products" />
      <div className="p-6 max-w-3xl">
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => navigate("/admin/products")} className="text-primary text-xl">‹</button>
          <h1 className="text-xl font-bold text-ink">商品分類管理</h1>
        </div>

        {/* 新增 / 編輯表單(共用) */}
        <div className="card space-y-4 mb-6">
          <div className="font-bold text-ink">
            {form.key ? `編輯分類:${form.title || form.key}` : "新增分類"}
          </div>
          <div>
            <label className="label">分類名稱 *(中文,顯示在 Bot 卡片與選購頁)</label>
            <input
              className="input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="即飲頂級燕窩"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">副標(英文,選填)</label>
              <input
                className="input"
                value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                placeholder="Ready-to-Drink"
              />
            </div>
            <div>
              <label className="label">排序(數字小在前)</label>
              <input
                className="input"
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                placeholder="10、20、30…"
              />
            </div>
          </div>
          <div>
            <label className="label">主圖檔名(GCS bucket 內,選填)</label>
            <input
              className="input"
              value={form.image_filename}
              onChange={(e) => setForm({ ...form, image_filename: e.target.value })}
              placeholder="product-liu-bai.jpg"
            />
            {form.image_filename && (
              <img
                src={`${PRODUCT_IMAGE_BASE}/${form.image_filename}`}
                alt="預覽"
                className="mt-2 w-40 h-30 object-cover rounded border border-line"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            )}
          </div>
          <label className="flex items-center gap-3 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              className="w-4 h-4"
            />
            上架中(取消勾選即停用 — Bot / 購物車不顯示此分類,但底下商品資料保留)
          </label>

          {/* 食用方法(結構版:可增減段落)*/}
          <div className="border-t border-line pt-4 space-y-3">
            <div className="font-bold text-ink text-sm">食用方法(選填 — 顯示在商品的「食用方法」按鈕)</div>
            <div>
              <label className="label">引言</label>
              <textarea
                className="input resize-none"
                rows={2}
                value={form.usageIntro}
                onChange={(e) => setForm({ ...form, usageIntro: e.target.value })}
                placeholder="此分類食用方法的開頭說明…"
              />
            </div>
            {form.usageSections.map((sec, i) => (
              <div key={i} className="border border-line rounded-lg p-3 space-y-2 bg-cream/40">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted">第 {i + 1} 段</span>
                  <button onClick={() => removeSection(i)} className="text-coral text-xs underline">
                    刪除這段
                  </button>
                </div>
                <input
                  className="input"
                  value={sec.title}
                  onChange={(e) => updateSection(i, "title", e.target.value)}
                  placeholder="小標題(例:保存期限)"
                />
                <textarea
                  className="input resize-none"
                  rows={3}
                  value={sec.body}
                  onChange={(e) => updateSection(i, "body", e.target.value)}
                  placeholder="內文(例:未開封常溫 / 冷藏可保存 2 年;開封後請當下食用完畢)"
                />
              </div>
            ))}
            <button onClick={addSection} className="btn-secondary text-sm w-full">
              ＋ 新增一段
            </button>
          </div>

          <div className="flex gap-3">
            {form.key && (
              <button onClick={resetForm} className="btn-secondary flex-1" disabled={submitting}>
                取消編輯
              </button>
            )}
            <button onClick={save} className="btn-primary flex-1" disabled={submitting}>
              {submitting ? "儲存中…" : form.key ? "儲存變更" : "新增分類"}
            </button>
          </div>
        </div>

        {/* 分類列表 */}
        <div className="card">
          {loading ? (
            <div className="py-10 text-center text-muted">載入中…</div>
          ) : cats.length === 0 ? (
            <div className="py-8 text-center space-y-4">
              <div className="text-muted">尚未有分類資料</div>
              <button onClick={runSeed} disabled={seeding} className="btn-primary">
                {seeding ? "匯入中…" : "匯入既有分類(即飲 / 鮮燉 / 自燉)"}
              </button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted text-xs border-b border-line">
                  <th className="py-2">排序</th>
                  <th className="py-2">分類名稱</th>
                  <th className="py-2">副標</th>
                  <th className="py-2 text-center">狀態</th>
                  <th className="py-2 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {cats.map((c) => (
                  <tr key={c.key} className="border-b border-line/50">
                    <td className="py-3 text-muted text-xs">{c.sort_order ?? "—"}</td>
                    <td className="py-3 font-medium">
                      {c.title}
                      <span className="text-muted text-xs ml-2">{c.key}</span>
                    </td>
                    <td className="py-3 text-muted text-xs">{c.subtitle || "—"}</td>
                    <td className="py-3 text-center">
                      <button
                        onClick={() => toggleActive(c)}
                        className={`text-xs px-2 py-1 rounded ${
                          (c.active ?? true) ? "bg-primary text-white" : "bg-muted/30 text-muted"
                        }`}
                      >
                        {(c.active ?? true) ? "上架中" : "已停用"}
                      </button>
                    </td>
                    <td className="py-3 text-right space-x-3 whitespace-nowrap">
                      <button onClick={() => startEdit(c)} className="text-primary text-xs underline">
                        編輯
                      </button>
                      <button onClick={() => remove(c)} className="text-coral text-xs underline">
                        刪除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="text-xs text-muted mt-3 text-right">共 {cats.length} 個分類</div>
        </div>
      </div>
    </div>
  );
}
